import { useState, useEffect } from 'react'
import { Calendar, ExternalLink } from 'lucide-react'
import AppLayout from './AppLayout'
import AutoGrid from './AutoGrid'
import { supabase } from '../integrations/supabase/client'

interface NewsItem {
  id: string
  title: string
  url: string
  publish_date: string | null
  scraped_at: string
  source: string
  body: string | null
}

const SOURCE_LABELS: Record<string, { label: string; color: string; baseUrl: string }> = {
  'asst-brianza':       { label: 'ASST Brianza',  color: '#2563eb', baseUrl: 'https://www.asst-brianza.it' },
  'csv-mb-territorio':  { label: 'Territorio',    color: '#059669', baseUrl: '' },
  'csv-mb-cittadini':   { label: 'Cittadini',     color: '#d97706', baseUrl: '' },
  'csv-mb-org-notizie': { label: 'Org. Notizie',  color: '#7c3aed', baseUrl: '' },
  'csv-mb-org-bandi':   { label: 'Org. Bandi',    color: '#dc2626', baseUrl: '' },
  'csv-mb-org-corsi':   { label: 'Org. Corsi',    color: '#0284c7', baseUrl: '' },
}

const NEWS_LIMIT = 15

function getFullUrl(item: NewsItem): string {
  const url = item.url
  if (url.startsWith('http')) return url
  const source = SOURCE_LABELS[item.source]
  return source ? source.baseUrl + url : url
}

function formatNewsSourceLabel(source: string): string {
  if (source.startsWith('csv-')) return `CSV · ${SOURCE_LABELS[source]?.label ?? source}`
  return SOURCE_LABELS[source]?.label ?? source
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
}


function NewsCard({ item }: { item: NewsItem }) {
  const src = SOURCE_LABELS[item.source]
  const accentColor = src?.color ?? 'var(--accent)'

  return (
    <a
      href={getFullUrl(item)}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'var(--bsd-card-height, 270px)',
        borderLeft: `3px solid ${accentColor}`,
        textDecoration: 'none',
        color: 'inherit',
        position: 'relative',
        overflow: 'hidden',
        border: '1px solid var(--border-md)',
        background: 'var(--card-bg)',
        borderRadius: 16,
        padding: 18,
        boxShadow: 'var(--card-shadow)',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        cursor: 'pointer',
      }}
    >
      {/* Source badge */}
      <div style={{ flexShrink: 0, marginBottom: 10 }}>
        <span style={{
          display: 'inline-block',
          fontSize: 9,
          fontWeight: 700,
          color: '#fff',
          background: accentColor,
          borderRadius: 4,
          padding: '2px 7px',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}>
          {formatNewsSourceLabel(item.source)}
        </span>
      </div>

      {/* Title */}
      <p style={{
        margin: 0,
        marginBottom: item.body ? 6 : 0,
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--text-0)',
        lineHeight: 1.5,
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: item.body ? 3 : 5,
        WebkitBoxOrient: 'vertical' as const,
      }}>
        {item.title}
      </p>

      {/* Excerpt */}
      {item.body && (
        <p style={{
          flex: 1,
          margin: 0,
          fontSize: 11,
          color: 'var(--text-3)',
          lineHeight: 1.55,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical' as const,
        }}>
          {item.body}
        </p>
      )}
      {!item.body && <div style={{ flex: 1 }} />}

      {/* Footer */}
      <div style={{
        flexShrink: 0,
        marginTop: 10,
        paddingTop: 8,
        borderTop: '1px solid var(--border)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 6,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: 'var(--text-3)', fontSize: 11 }}>
          <Calendar size={11} />
          {formatDate(item.publish_date)}
        </div>
        <ExternalLink size={12} style={{ color: 'var(--text-3)', flexShrink: 0 }} />
      </div>
    </a>
  )
}

export default function NewsPage() {
  const [news, setNews] = useState<NewsItem[]>([])

  const fetchNews = async () => {
    const { data, error } = await supabase
      .from('news')
      .select('*')
      .order('publish_date', { ascending: false })
      .limit(NEWS_LIMIT)

    if (!error && data) {
      setNews(data.map(d => ({ ...d, body: (d as any).body ?? null })) as NewsItem[])
    }
  }

  useEffect(() => { fetchNews() }, [])

  return (
    <AppLayout>
      {() => (
        <section className="telemetry-dashboard">
          <div className="telemetry-inner">

            {/* Header */}
            

           
              <AutoGrid>
                {news.map(item => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </AutoGrid>
            

          </div>
        </section>
      )}
    </AppLayout>
  )
}
